import { useIntl } from 'dibs-react-intl';
import { type SelectOption } from 'dibs-elements/exports/MultiSelect';
import { type BasicSelectOption } from 'dibs-elements/exports/BasicSelect';
import {
    type PrimaryBusinessFocus,
    type Currency,
    businessTypeMap,
    FURNITURE_VINTAGE,
    FURNITURE_CONTEMPORARY,
    FINE_ART,
    JEWELRY_ESTATE,
    JEWELRY_CONTEMPORARY,
    FASHION_VINTAGE,
    FASHION_CONTEMPORARY,
} from '../form/SellerApplicationFormReducer';
import {
    yesNoMessages,
    businessFocusMessages,
    businessTypeMessages,
    preferredLanguageMessages,
    getLabel,
    getValue,
} from './sellerApplicationOptionsHelper';

export const useGetYesNoOptions = (): SelectOption[] => {
    const intl = useIntl();
    return [
        {
            label: intl.formatMessage(yesNoMessages.yes),
            value: 'true',
        },
        {
            label: intl.formatMessage(yesNoMessages.no),
            value: 'false',
        },
    ];
};

export const useGetNumRangeOptions = (numRangeArr: readonly string[]): BasicSelectOption[] => {
    const options: BasicSelectOption[] = [];
    for (const numRange of numRangeArr) {
        options.push({
            // The replace here inserts spaces between the two numbers
            label: numRange.replace(/(\d*)-(\d*)/, '$1 - $2'),
            value: numRange,
        });
    }
    return options;
};

export const useGetItemPriceOptions = (
    selectedCurrency: Currency = 'USD'
): { label: string; value: string }[] => {
    const intl = useIntl();
    const priceRanges = [
        { to: 500 },
        { from: 500, to: 2000 },
        { from: 2000, to: 5000 },
        { from: 5000, to: 10000 },
        { from: 10000 },
    ];
    return priceRanges.map(range => ({
        label: getLabel(range, selectedCurrency, intl),
        value: getValue(range),
    }));
};

export const useGetBusinessFocusOptions = (): BasicSelectOption[] => {
    const intl = useIntl();
    return [
        {
            label: intl.formatMessage(businessFocusMessages.businessFocusFurnitureOptionLabel),
            options: [
                {
                    label: intl.formatMessage(
                        businessFocusMessages.businessFocusFurnitureSelection1
                    ),
                    value: FURNITURE_VINTAGE,
                },
                {
                    label: intl.formatMessage(
                        businessFocusMessages.businessFocusFurnitureSelection2
                    ),
                    value: FURNITURE_CONTEMPORARY,
                },
            ],
        },
        {
            label: intl.formatMessage(businessFocusMessages.businessFocusFashionOptionLabel),
            options: [
                {
                    label: intl.formatMessage(businessFocusMessages.businessFocusFashionSelection1),
                    value: FASHION_CONTEMPORARY,
                },
                {
                    label: intl.formatMessage(businessFocusMessages.businessFocusFashionSelection2),
                    value: FASHION_VINTAGE,
                },
            ],
        },
        {
            label: intl.formatMessage(businessFocusMessages.businessFocusJewelryOptionLabel),
            options: [
                {
                    label: intl.formatMessage(businessFocusMessages.businessFocusJewelrySelection1),
                    value: JEWELRY_ESTATE,
                },
                {
                    label: intl.formatMessage(businessFocusMessages.businessFocusJewelrySelection2),
                    value: JEWELRY_CONTEMPORARY,
                },
            ],
        },
        {
            label: intl.formatMessage(businessFocusMessages.businessFocusFineArtOptionLabel),
            options: [
                {
                    label: intl.formatMessage(
                        businessFocusMessages.businessFocusFineArtOptionLabel
                    ),
                    value: FINE_ART,
                },
            ],
        },
    ];
};

export const useGetBusinessTypeOptions = ({
    selectedBusinessFocus,
}: {
    selectedBusinessFocus?: PrimaryBusinessFocus;
}): BasicSelectOption[] | undefined => {
    const intl = useIntl();
    if (!selectedBusinessFocus) {
        return undefined;
    }

    let label = '';
    const options = [];
    for (const businessType of businessTypeMap[selectedBusinessFocus]) {
        options.push({
            label: intl.formatMessage(businessTypeMessages[businessType]),
            value: businessType,
        });
    }
    switch (selectedBusinessFocus) {
        case FASHION_CONTEMPORARY:
        case FASHION_VINTAGE:
            label = intl.formatMessage(businessFocusMessages.businessFocusFashionOptionLabel);
            break;
        case FINE_ART:
            label = intl.formatMessage(businessFocusMessages.businessFocusFineArtOptionLabel);
            break;
        case FURNITURE_CONTEMPORARY:
        case FURNITURE_VINTAGE:
            label = intl.formatMessage(businessFocusMessages.businessFocusFurnitureOptionLabel);
            break;
        case JEWELRY_CONTEMPORARY:
        case JEWELRY_ESTATE:
            label = intl.formatMessage(businessFocusMessages.businessFocusJewelryOptionLabel);
            break;
    }
    return [{ label, options }];
};

export const useGetPreferredLanguageOptions = (): BasicSelectOption[] => {
    const intl = useIntl();
    return [
        {
            label: intl.formatMessage(preferredLanguageMessages.english),
            value: 'English',
        },
        {
            label: intl.formatMessage(preferredLanguageMessages.italian),
            value: 'Italian',
        },
    ];
};
